import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KpiCard } from './KpiCard';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { BookOpen, CheckCircle2, Clock3, GraduationCap, UserPlus, Users } from 'lucide-react';
import type { SelfPacedExecMetrics } from '@/hooks/useExecutiveSelfPacedMetrics';
import { formatBucketLabel, Granularity } from '@/lib/timeSeries';

const CHART_COLORS = [
  'hsl(var(--muted-foreground))',
  'hsl(var(--info))',
  'hsl(var(--secondary))',
  'hsl(var(--accent))',
  'hsl(var(--warning))',
  'hsl(var(--success))',
];

interface Props {
  data?: SelfPacedExecMetrics;
  isLoading: boolean;
  granularity: Granularity;
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('es-CO') : 'Nunca';
}

export function SelfPacedTab({ data, isLoading, granularity }: Props) {
  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((item) => <Skeleton key={item} className="h-32" />)}
        </div>
        <Skeleton className="h-72" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  const { totals, routes, distribution, orgs, stalled } = data;
  const completionRate = totals.inscritos > 0 ? Math.round((totals.completados / totals.inscritos) * 100) : 0;
  const series = data.series.map((item) => ({
    ...item,
    label: formatBucketLabel(item.bucket, granularity),
  }));
  const routeChart = routes.slice(0, 12).map((route) => ({
    ...route,
    name: route.program_name.length > 24 ? `${route.program_name.slice(0, 24)}…` : route.program_name,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Usuarios inscritos"
          value={totals.usuarios.toLocaleString('es-CO')}
          hint={`${totals.inscritos.toLocaleString('es-CO')} inscripciones`}
          icon={<Users className="w-4 h-4" />}
          accent="primary"
        />
        <KpiCard
          label="Nuevas inscripciones"
          value={totals.nuevas.toLocaleString('es-CO')}
          hint="En el rango seleccionado"
          icon={<UserPlus className="w-4 h-4" />}
          accent="success"
        />
        <KpiCard
          label="Progreso promedio"
          value={`${totals.avg_progress}%`}
          hint={`${totals.activos_30d} activos en 30 días`}
          icon={<BookOpen className="w-4 h-4" />}
          accent="accent"
        />
        <KpiCard
          label="Tasa de finalización"
          value={`${completionRate}%`}
          hint={`${totals.completados} rutas completadas`}
          icon={<CheckCircle2 className="w-4 h-4" />}
          accent="info"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Inscripciones en el tiempo</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="label" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Line type="monotone" dataKey="inscripciones" name="Inscripciones" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Distribución de avance</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={distribution} dataKey="usuarios" nameKey="rango" outerRadius={100} label>
                  {distribution.map((_, index) => <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader><CardTitle className="text-base">Inscritos y completados por ruta</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={routeChart} layout="vertical" margin={{ left: 16, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={170} fontSize={10} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="inscritos" name="Inscritos" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
              <Bar dataKey="completados" name="Completados" fill="hsl(var(--success))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Rutas con mayor uso</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Ruta</TableHead><TableHead>Inscritos</TableHead><TableHead>Avance</TableHead><TableHead>Finalizadas</TableHead></TableRow></TableHeader>
              <TableBody>
                {routes.map((route) => (
                  <TableRow key={route.program_id}>
                    <TableCell className="font-medium min-w-[220px]">{route.program_name}</TableCell>
                    <TableCell>{route.inscritos}</TableCell>
                    <TableCell>{route.avg_progress}%</TableCell>
                    <TableCell>{route.completados} ({route.inscritos > 0 ? Math.round((route.completados / route.inscritos) * 100) : 0}%)</TableCell>
                  </TableRow>
                ))}
                {routes.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Sin inscripciones.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader><CardTitle className="text-base">Organizaciones con inscritos</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Organización</TableHead><TableHead>Usuarios</TableHead><TableHead>Inscripciones</TableHead><TableHead>Avance</TableHead></TableRow></TableHeader>
              <TableBody>
                {orgs.map((org) => (
                  <TableRow key={org.name}>
                    <TableCell className="font-medium">{org.name}</TableCell>
                    <TableCell>{org.usuarios}</TableCell>
                    <TableCell>{org.inscritos}</TableCell>
                    <TableCell>{org.avg_progress}%</TableCell>
                  </TableRow>
                ))}
                {orgs.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">Sin datos.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Clock3 className="w-4 h-4 text-warning" /> Usuarios sin actividad reciente</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Organización</TableHead><TableHead>Ruta</TableHead><TableHead>Avance</TableHead><TableHead>Última actividad</TableHead></TableRow></TableHeader>
            <TableBody>
              {stalled.map((user, index) => (
                <TableRow key={`${user.email}-${user.program_name}-${index}`}>
                  <TableCell><div className="font-medium">{user.full_name ?? 'Sin nombre'}</div><div className="text-xs text-muted-foreground">{user.email ?? '—'}</div></TableCell>
                  <TableCell>{user.org ?? 'Sin organización'}</TableCell>
                  <TableCell className="min-w-[180px]">{user.program_name ?? '—'}</TableCell>
                  <TableCell>{user.pct}%</TableCell>
                  <TableCell>{formatDate(user.last_activity)}</TableCell>
                </TableRow>
              ))}
              {stalled.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No hay usuarios estancados.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <GraduationCap className="w-4 h-4" />
        <span>{totals.interesados} interesados registrados en rutas de autoaprendizaje.</span>
      </div>
    </div>
  );
}
